<script setup lang="ts">
    const props = defineProps<{
        news?: StoryblokMultilinkStory;
        isOverviewPage?: boolean;
        categories?: DatasourceEntry[];
        blok?: BlogTeaserStoryblok;
    }>();

    const { isPtaFamily } = usePlatformMeta();
    const { t } = useI18n();
    const { renderToString } = useRichTextRenderer();

    const resolveCategoryKey = (category: string | number) => {
        const matchedCategory = props.categories?.find((data) => data.value === category);
        return matchedCategory ? matchedCategory.dimension_value || matchedCategory.value : category;
    };

    const useBlok = computed(() => !props.news && !!props.blok);

    const blokHref = computed(() => {
        return useStoryblokUrl(props.blok?.link);
    });

    const href = computed(() => {
        if (useBlok.value) return blokHref.value || '/';
        const raw = props.news?.full_slug || '';
        return useStoryblokUrl(raw);
    });

    const title = computed(() => {
        if (useBlok.value) return props.blok?.title || '';
        return props.news?.content?.Headline || '';
    });

    const abstractHtml = computed(() => {
        if (useBlok.value) {
            return props.blok?.text ? renderToString(props.blok.text) : '';
        }
        return props.news?.parsedAbstract || '';
    });

    const image = computed(() => {
        if (useBlok.value) return props.blok?.image;
        return props.news?.content?.Image;
    });

    const categoriesList = computed<(string | number)[]>(() => {
        if (useBlok.value) return props.blok?.categories ?? []; // nur angezeigt, falls vorhanden
        return props.news?.content?.Categories ?? [];
    });
    const hasCategories = computed(() => Array.isArray(categoriesList.value) && categoriesList.value.length > 0);

    const dateRaw = computed<string | Date | null>(() => {
        if (useBlok.value) return props.blok?.date ?? null;
        return props.news?.content?.Date ?? null;
    });
    const formattedDate = computed(() => {
        if (!dateRaw.value) return '';
        const d = typeof dateRaw.value === 'string' ? new Date(dateRaw.value) : dateRaw.value;
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    });

    const ctaText = computed(() => {
        if (useBlok.value) return props.blok?.alternativeCTAText || t('pageNews.cta');
        return t('pageNews.cta');
    });
</script>

<template>
    <NuxtLink
        class="news-item"
        :class="[
            blok?.theme ? '--theme-' + blok.theme : isPtaFamily ? '--theme-white-pink' : '',
            isOverviewPage ? '--overview-page' : ''
        ]"
        :to="href"
    >
        <div class="content-wrapper">
            <div class="categories-heading-wrapper">
                <div
                    v-show="news"
                    class="header"
                >
                    <div
                        v-if="hasCategories"
                        class="categories"
                    >
                        <p :title="categoriesList.map((c: any) => resolveCategoryKey(c)).join(', ')">
                            {{ categoriesList.map((c: any) => resolveCategoryKey(c)).join(', ') }}
                        </p>
                    </div>

                    <span
                        v-if="formattedDate"
                        class="date"
                    >
                        {{ formattedDate }}
                    </span>
                </div>

                <h3 :class="news ? 'news-card-header' : ''">{{ title }}</h3>
            </div>

            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="abstractHtml"
                class="abstract"
                v-html="abstractHtml"
            ></div>
            <!-- eslint-enable vue/no-v-html -->

            <PrimaryButton
                :button-text="ctaText"
                :button-href="href"
                :has-icon="true"
                product-button
                :retain-size="true"
            />
        </div>

        <div
            v-if="image?.filename"
            class="image-wrapper"
        >
            <div class="image-mask">
                <StoryblokImage
                    :media="image"
                    type="mainPicture"
                    :alt="t('pageNews.alt')"
                />
            </div>

            <GradientShape
                :shape="'leaf'"
                :theme="blok?.theme || 'white-pink'"
                :gradient-angle="'0deg'"
            />
        </div>
    </NuxtLink>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/news/news-card.scss';
</style>
